// src/components/FinishOnboardingModal.js
// ============================================================================
// FINISH-ONBOARDING GUARD
//
// Shown to a SIGNED-IN member whose account never finished onboarding:
// either users/{uid} is missing (a Google signup interrupted before its doc
// was written — 5 of the first 9 real Google accounts, Mar–Aug 2026) or it is
// still pending (onboardingComplete:false — the server-side doc every OAuth
// signup gets the moment its Auth account exists).
//
// Why a global modal: LandingScreen sends every signed-in session straight
// to Lobby, and nothing there, in the header, or on any other screen ever
// asked a pending account to finish. Until 2026-08-17 the ONLY surface that
// noticed was ProfileSettings — and it said "Not Logged In". A pending
// account has no username, no 18+ DOB, and no ToS acceptance, so it must not
// use member surfaces; this overlay is the backstop on every route the
// completion flow itself doesn't own (see HIDE routes in App.js).
//
// It mirrors TosUpdateModal: non-dismissable, but never a trap — "Log out"
// is always available, and the doc listener in App.js clears it the moment
// completeOAuthSignup flips the flag.
// ============================================================================

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { isDesktopApp } from '../utils/platform';
import { isPhoneOnlyRecord, signOutWithDesktopPush, PHONE_NO_ACCOUNT_TEXT } from '../utils/phoneNumber';

const C = {
  BG: 'rgba(0,0,0,0.88)',
  CARD: '#111',
  BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  MUTED: '#888',
};

export default function FinishOnboardingModal({ visible, onComplete }) {
  const user = auth.currentUser;
  const email = user?.email || null;
  // Phone sign-in never creates an account (DESKTOP_APP_DESIGN_2026-10-20.md
  // S2.4 item 3): a phone-only record with no email that reaches the app (a
  // ghost whose client delete failed) sees the pinned message and Log out
  // only, never "Complete My Profile". The purgePhoneGhosts sweep removes it.
  const phoneOnly = isPhoneOnlyRecord(user?.providerData, email);

  // Log out is a member's button: inside the desktop app it tells the app
  // first (pushSession(null, { userAction: true })), then signs out.
  const logOut = () => signOutWithDesktopPush({ signOut, auth, isDesktop: isDesktopApp() }).catch(() => {});

  if (phoneOnly) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={() => {}}>
        <View style={styles.backdrop}>
          <View style={styles.card}>
            <View style={styles.iconWrap}>
              <Ionicons name="call-outline" size={40} color={C.GOLD} />
            </View>
            <Text style={styles.body}>{PHONE_NO_ACCOUNT_TEXT}</Text>
            <TouchableOpacity style={styles.primaryBtn} onPress={logOut} activeOpacity={0.85}>
              <Text style={styles.primaryBtnText}>Log out</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <View style={styles.iconWrap}>
            <Ionicons name="person-add" size={40} color={C.GOLD} />
          </View>
          <Text style={styles.heading}>Finish Setting Up Your Account</Text>
          <Text style={styles.body}>
            You're signed in{email ? ` as ${email}` : ''}, but your profile isn't complete yet.
            It only takes a minute — pick a username and confirm a few details, and you're in.
          </Text>

          <TouchableOpacity style={styles.primaryBtn} onPress={onComplete} activeOpacity={0.85}>
            <Text style={styles.primaryBtnText}>Complete My Profile</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryBtn}
            onPress={logOut}
            activeOpacity={0.7}
          >
            <Ionicons name="log-out-outline" size={16} color={C.MUTED} />
            <Text style={styles.secondaryBtnText}>Log out</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: C.BG, alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: {
    width: '100%', maxWidth: 460,
    backgroundColor: C.CARD, borderRadius: 14, borderWidth: 1, borderColor: C.BORDER, padding: 24,
    alignItems: 'center',
  },
  iconWrap: { marginBottom: 12 },
  heading: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginBottom: 8, textAlign: 'center' },
  body: { color: C.TEXT, fontSize: 14, lineHeight: 21, textAlign: 'center', marginBottom: 18 },
  primaryBtn: {
    alignSelf: 'stretch', backgroundColor: C.GOLD, borderRadius: 8, paddingVertical: 13,
    alignItems: 'center', justifyContent: 'center',
  },
  primaryBtnText: { color: '#000', fontSize: 15, fontWeight: 'bold' },
  secondaryBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 14, paddingVertical: 6, paddingHorizontal: 10,
  },
  secondaryBtnText: { color: C.MUTED, fontSize: 13 },
});
